import type { OutboxHydrationState, OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import isInterruptedRecording from '@UI/Helpers/Outbox/isInterruptedRecording';
import { InterviewProcessingState } from '@Domain/Enums/Interviews/InterviewProcessingState';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';

/** What the upload page shows; each variant is rendered by exactly one card. */
export type UploadStage =
	| { readonly type: 'loading' }
	| { readonly type: 'not-found' }
	| { readonly type: 'orphaned'; readonly outboxItemId: string }
	| { readonly type: 'interrupted'; readonly outboxItem: OutboxItem }
	| { readonly type: 'transferring'; readonly outboxItem: OutboxItem }
	| { readonly type: 'awaiting-server' }
	| { readonly type: 'uploading-elsewhere' }
	| { readonly type: 'no-recording' }
	| { readonly type: 'received' }
	| { readonly type: 'no-workflow' }
	| { readonly type: 'processing'; readonly interviewId: number };

type Args = {
	/** The queue item of this device for the interview, when there is one. */
	readonly outboxItem: OutboxItem | null;
	/** The interview as the server lists it, when it has been read. */
	readonly serverInterview: InterviewView | null;
	/** The server list has answered, successfully or not. */
	readonly isServerSettled: boolean;
	readonly isLocalStoreHydrated: boolean;
	readonly outboxHydration: OutboxHydrationState;
	/** A capture runs in this tab or in another one. */
	readonly isCaptureInProgress: boolean;
};

/** The states in which a treatment has started: the interview leaves the upload page for its follow-up. */
const PROCESSED_STATES: ReadonlySet<string> = Object.freeze(
	new Set<string>([
		InterviewProcessingState.RUNNING,
		InterviewProcessingState.STALLED,
		InterviewProcessingState.FAILED,
		InterviewProcessingState.PARTIAL,
		InterviewProcessingState.SUCCEEDED,
		InterviewProcessingState.HANDLED,
	]),
);

const resolveServerStage = (serverInterview: InterviewView, outboxItem: OutboxItem | null): UploadStage => {
	if (PROCESSED_STATES.has(serverInterview.processingState.value)) {
		return { type: 'processing', interviewId: serverInterview.id };
	}

	switch (serverInterview.processingState.value) {
		case 'no_recording':
			return { type: 'no-recording' };
		case 'uploading':
			// A queue item confirmed here while the list still says "uploading" is a list that has not caught up.
			return outboxItem !== null ? { type: 'awaiting-server' } : { type: 'uploading-elsewhere' };
		case 'awaiting_choice':
			return { type: 'received' };
		case 'no_workflow':
			return { type: 'no-workflow' };
		default:
			return { type: 'not-found' };
	}
};

/**
 * Picks the one stage of the upload page. A deleted interview waits for a decision before anything else. The transfer queue of this device wins until its item is
 * confirmed (the server may know nothing of the interview yet); from then on the server decides.
 */
const resolveUploadStage = ({
	outboxItem,
	serverInterview,
	isServerSettled,
	isLocalStoreHydrated,
	outboxHydration,
	isCaptureInProgress,
}: Args): UploadStage => {
	if (!isLocalStoreHydrated || outboxHydration === 'pending') {
		return { type: 'loading' };
	}

	if (outboxItem !== null && outboxItem.state === 'orphaned') {
		return { type: 'orphaned', outboxItemId: outboxItem.id };
	}

	if (outboxItem !== null && isInterruptedRecording(outboxItem.state, isCaptureInProgress)) {
		return { type: 'interrupted', outboxItem };
	}

	if (outboxItem !== null && outboxItem.state !== 'confirmed') {
		return { type: 'transferring', outboxItem };
	}

	if (!isServerSettled) {
		return { type: 'loading' };
	}

	if (serverInterview !== null) {
		return resolveServerStage(serverInterview, outboxItem);
	}

	return outboxItem !== null ? { type: 'awaiting-server' } : { type: 'not-found' };
};

export default resolveUploadStage;
